import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * Coach: Inbox of pending (unsigned) training weeks.
 * Route: /coach/berichte
 */
const CoachReportInbox = () => {
    const { fetchWithAuth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [signingId, setSigningId] = useState(null);
    const [comment, setComment] = useState('');

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth('/api/coach/reports/pending/');
            const data = await res.json();
            setPending(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const sign = async (weekId, status = 'SIGNED') => {
        const res = await fetchWithAuth(`/api/coach/reports/${weekId}/review/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status, comment }),
        });
        if (res.ok) {
            setComment('');
            setSigningId(null);
            load();
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
            <button onClick={() => navigate('/coach')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-plantlix-500 transition flex items-center gap-1">
                ← Dashboard
            </button>

            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">📋 Offene Berichte</h1>
                <span className="text-sm text-gray-400">{pending.length} ausstehend</span>
            </div>

            {pending.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                    <p className="text-5xl mb-4">✅</p>
                    <p className="font-semibold text-lg text-gray-700 dark:text-gray-300">Alles abgezeichnet!</p>
                    <p className="text-sm mt-1">Keine ausstehenden Berichtswochen.</p>
                </div>
            )}

            <div className="space-y-3">
                {pending.map(w => (
                    <div key={w.id} className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-2xl p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold text-gray-900 dark:text-white">
                                    {w.apprentice_name} · KW {w.week_number}/{w.year}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {w.start_date} – {w.end_date} · {w.daily_report_count}/7 Tage · {w.completion_percentage}% vollständig
                                </p>
                            </div>
                            <div className="flex gap-2">
                                {signingId !== w.id ? (
                                    <>
                                        <button onClick={() => sign(w.id, 'SIGNED')}
                                            className="px-4 py-2 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white text-sm font-bold transition">
                                            ✓ Abzeichnen
                                        </button>
                                        <button onClick={() => setSigningId(w.id)}
                                            className="px-4 py-2 rounded-xl border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 text-sm font-bold hover:bg-amber-50 dark:hover:bg-amber-900/20 transition">
                                            💬 Kommentar
                                        </button>
                                    </>
                                ) : (
                                    <button onClick={() => setSigningId(null)} className="text-xs text-gray-400 hover:text-gray-600">Abbrechen</button>
                                )}
                            </div>
                        </div>

                        {/* Inline comment */}
                        {signingId === w.id && (
                            <div className="flex gap-3">
                                <input
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                    placeholder="Kommentar / Feedback…"
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-plantlix-400"
                                />
                                <button onClick={() => sign(w.id, 'SIGNED')} className="px-4 py-2.5 rounded-xl bg-plantlix-500 text-white text-sm font-bold">
                                    ✓ Mit Kommentar
                                </button>
                                <button onClick={() => sign(w.id, 'NEEDS_REVISION')} className="px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold">
                                    ↩ Überarbeitung
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CoachReportInbox;
